import { formatInstant, resolveDisplayZone } from "@checklist/shared";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext.js";
import {
  AuthShell,
  ErrorBanner,
  linkCls,
  SuccessBanner,
} from "../components/AuthShell.js";
import { Button, LinkButton } from "../components/ui/Button.js";
import {
  acceptInvite,
  ApiError,
  getInvite,
  type ApiInvitePublic,
} from "../lib/api.js";
import { useSignupMode } from "../hooks/useSignupMode.js";
import { signupLabel } from "../lib/signupCopy.js";

/** Invite accept page (PLAN.md Task 4.3). Public preview; accept requires auth. */
export function InvitePage() {
  const { mode: signupMode } = useSignupMode();
  const { token } = useParams<{ token: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const next = `/invite/${token ?? ""}`;

  const [info, setInfo] = useState<ApiInvitePublic | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [acceptedMsg, setAcceptedMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setLoadError("Invalid invite link.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await getInvite(token);
        if (!cancelled) setInfo(data);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.code === "INVITE_EXPIRED") setLoadError("This invite has expired.");
          else if (err.code === "INVITE_EXHAUSTED") {
            setLoadError("This invite has reached its use limit.");
          } else if (err.status === 404) setLoadError("Invite not found.");
          else setLoadError(err.message);
        } else {
          setLoadError("Failed to load invite.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onAccept() {
    if (!token) return;
    setAccepting(true);
    setAcceptError(null);
    setAcceptedMsg(null);
    try {
      const result = await acceptInvite(token);
      setAcceptedMsg(
        result.alreadyMember
          ? "You already have access."
          : "Invite accepted — opening checklist…"
      );
      navigate(`/checklists/${result.checklistId}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
          return;
        }
        if (err.code === "INVITE_EXPIRED") setAcceptError("This invite has expired.");
        else if (err.code === "INVITE_EXHAUSTED") {
          setAcceptError("This invite has reached its use limit.");
        } else if (err.code === "FORBIDDEN") {
          setAcceptError("You no longer have permission — ask the owner for a new invite.");
        } else {
          setAcceptError(err.message);
        }
      } else {
        setAcceptError("Could not accept invite.");
      }
      setAccepting(false);
    }
  }

  const roleLabel =
    info?.role === "write" ? "Can edit" : info?.role === "read" ? "View only" : "";

  return (
    <AuthShell
      title="Checklist invite"
      subtitle={
        info
          ? `You've been invited to "${info.checklistName}".`
          : "Open a shared checklist invitation."
      }
    >
      {loading || authLoading ? (
        <p className="text-sm text-muted">Loading invite…</p>
      ) : loadError ? (
        <div className="space-y-4">
          <ErrorBanner message={loadError} />
          <Link to="/" className={linkCls}>
            Go home
          </Link>
        </div>
      ) : info ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-line bg-slate-50 px-3 py-3 text-sm">
            <p className="font-medium text-ink">{info.checklistName}</p>
            <p className="mt-1 text-muted">
              Role: {roleLabel}
              {info.expiresAt
                ? ` · Expires ${formatInstant(info.expiresAt, resolveDisplayZone())}`
                : ""}
            </p>
          </div>

          <ErrorBanner message={acceptError} />
          <SuccessBanner message={acceptedMsg} />

          {user ? (
            <Button
              type="button"
              fullWidth
              disabled={accepting}
              onClick={() => void onAccept()}
            >
              {accepting ? "Accepting…" : "Accept invite"}
            </Button>
          ) : (
            <div className="space-y-3">
              <LinkButton to={`/login?next=${encodeURIComponent(next)}`} fullWidth>
                Sign in to accept
              </LinkButton>
              <p className="text-center text-sm text-muted">
                <Link
                  to={`/register?next=${encodeURIComponent(next)}`}
                  className={linkCls}
                >
                  {signupLabel("invite", signupMode)}
                </Link>
              </p>
            </div>
          )}
        </div>
      ) : null}
    </AuthShell>
  );
}
